/**
 * Crop rectangles, quarter turns and flips — the geometry of the editor.
 *
 * The transform is produced as an affine matrix rather than as a sequence of
 * `ctx.rotate` / `ctx.translate` calls. Both end up doing the same thing, but a
 * matrix can be handed a corner and asked where it lands, which means the
 * rotation can be tested rather than squinted at. Compose-by-canvas-call is
 * exactly the kind of code that is subtly wrong at 270 degrees with a flip on,
 * and where nobody notices for a year.
 */

/** A crop, normalised to 0..1 of the *displayed* image — after any rotation. */
export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const FULL_CROP: CropRect = { x: 0, y: 0, width: 1, height: 1 };

/** Quarter turns clockwise. Arbitrary angles would need the crop to inset. */
export type Quarter = 0 | 90 | 180 | 270;

export interface Transform {
  crop: CropRect;
  rotate: Quarter;
  flipH: boolean;
}

export const IDENTITY: Transform = { crop: FULL_CROP, rotate: 0, flipH: false };

export function isIdentity(transform: Transform): boolean {
  return (
    transform.rotate === 0 &&
    !transform.flipH &&
    transform.crop.x === 0 &&
    transform.crop.y === 0 &&
    transform.crop.width === 1 &&
    transform.crop.height === 1
  );
}

/** Below this a crop is a mis-drag rather than an intention. */
export const MIN_CROP = 0.05;

/** Null is "whatever shape you drag"; zero means "keep what it already is". */
export const ASPECT_PRESETS: { label: string; value: number | null }[] = [
  { label: "Free", value: null },
  { label: "1:1", value: 1 },
  { label: "4:5", value: 4 / 5 },
  { label: "3:2", value: 3 / 2 },
  { label: "2:3", value: 2 / 3 },
  { label: "16:9", value: 16 / 9 },
  { label: "9:16", value: 9 / 16 },
];

/** The size the image presents at, which swaps on a quarter turn. */
export function displaySize(
  sourceWidth: number,
  sourceHeight: number,
  rotate: Quarter,
): { width: number; height: number } {
  return rotate === 90 || rotate === 270
    ? { width: sourceHeight, height: sourceWidth }
    : { width: sourceWidth, height: sourceHeight };
}

/** Pixel dimensions of the finished export. Never zero — a crop must have area. */
export function outputSize(
  transform: Transform,
  sourceWidth: number,
  sourceHeight: number,
): { width: number; height: number } {
  const display = displaySize(sourceWidth, sourceHeight, transform.rotate);
  return {
    width: Math.max(1, Math.round(display.width * transform.crop.width)),
    height: Math.max(1, Math.round(display.height * transform.crop.height)),
  };
}

/* ────────────────────────────── The matrix ────────────────────────────────── */

/** A canvas affine transform: x' = a·x + c·y + e, y' = b·x + d·y + f. */
export interface Matrix {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

export function applyMatrix(
  matrix: Matrix,
  x: number,
  y: number,
): { x: number; y: number } {
  return {
    x: matrix.a * x + matrix.c * y + matrix.e,
    y: matrix.b * x + matrix.d * y + matrix.f,
  };
}

/**
 * Where each source pixel lands on the output canvas.
 *
 * Built in three stages, outermost last: turn the image, mirror it if asked,
 * then slide the crop's top-left corner up to the origin. Feed the result to
 * `setTransform` and draw the source at (0, 0) — the canvas does the rest, at
 * whatever quality its resampler offers, which beats anything done by hand.
 */
export function renderMatrix(
  transform: Transform,
  sourceWidth: number,
  sourceHeight: number,
): Matrix {
  const display = displaySize(sourceWidth, sourceHeight, transform.rotate);

  // Rotation, with the translation that brings the turned image back into
  // positive coordinates.
  let matrix: Matrix;
  switch (transform.rotate) {
    case 90:
      matrix = { a: 0, b: 1, c: -1, d: 0, e: sourceHeight, f: 0 };
      break;
    case 180:
      matrix = { a: -1, b: 0, c: 0, d: -1, e: sourceWidth, f: sourceHeight };
      break;
    case 270:
      matrix = { a: 0, b: -1, c: 1, d: 0, e: 0, f: sourceWidth };
      break;
    default:
      matrix = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
  }

  // Mirror across the displayed image's vertical centre line: x becomes
  // width - x, which negates the row of the matrix that produces x.
  if (transform.flipH) {
    matrix = {
      a: -matrix.a,
      b: matrix.b,
      c: -matrix.c,
      d: matrix.d,
      e: display.width - matrix.e,
      f: matrix.f,
    };
  }

  // And the crop, which is only ever a translation — the output canvas is
  // already the cropped size, so there is nothing to scale.
  return {
    ...matrix,
    e: matrix.e - transform.crop.x * display.width,
    f: matrix.f - transform.crop.y * display.height,
  };
}

/* ────────────────────────────── Crop editing ──────────────────────────────── */

/** Keep a rectangle inside the frame, and never let it collapse to nothing. */
export function clampCrop(rect: CropRect): CropRect {
  const width = Math.min(1, Math.max(MIN_CROP, rect.width));
  const height = Math.min(1, Math.max(MIN_CROP, rect.height));
  return {
    width,
    height,
    x: Math.min(1 - width, Math.max(0, rect.x)),
    y: Math.min(1 - height, Math.max(0, rect.y)),
  };
}

/**
 * The largest centred crop of a given aspect.
 *
 * `aspect` is the ratio the *result* should have, in real pixels — so it has to
 * know the displayed shape to work out what fraction of it that is. A 1:1 crop
 * of a landscape photograph is a full-height square, not the whole frame.
 */
export function centredCrop(
  aspect: number,
  displayWidth: number,
  displayHeight: number,
): CropRect {
  const frameAspect = displayWidth / displayHeight;

  // Wider than the frame: limited by width. Taller: limited by height.
  const width = aspect >= frameAspect ? 1 : aspect / frameAspect;
  const height = aspect >= frameAspect ? frameAspect / aspect : 1;

  return {
    x: (1 - width) / 2,
    y: (1 - height) / 2,
    width,
    height,
  };
}

/** Which corner or edge is being dragged. */
export type Handle = "nw" | "ne" | "sw" | "se" | "n" | "s" | "w" | "e" | "move";

/**
 * Move or resize a crop by a normalised delta.
 *
 * Pure, so the drag behaviour that is otherwise only reachable by pointer can
 * be asserted on: that a corner drag past the opposite edge stops rather than
 * inverting, that an aspect stays locked, that moving never leaves the frame.
 */
export function dragCrop(
  rect: CropRect,
  handle: Handle,
  dx: number,
  dy: number,
  aspect: number | null,
  displayWidth: number,
  displayHeight: number,
): CropRect {
  if (handle === "move") {
    return clampCrop({ ...rect, x: rect.x + dx, y: rect.y + dy });
  }

  // Edges in normalised space, adjusted independently and reconciled after.
  let left = rect.x;
  let top = rect.y;
  let right = rect.x + rect.width;
  let bottom = rect.y + rect.height;

  if (handle.includes("w"))
    left = Math.min(right - MIN_CROP, Math.max(0, left + dx));
  if (handle.includes("e"))
    right = Math.max(left + MIN_CROP, Math.min(1, right + dx));
  if (handle.includes("n"))
    top = Math.min(bottom - MIN_CROP, Math.max(0, top + dy));
  if (handle.includes("s"))
    bottom = Math.max(top + MIN_CROP, Math.min(1, bottom + dy));

  let next: CropRect = {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top,
  };

  if (aspect !== null) {
    next = enforceAspect(next, handle, aspect, displayWidth, displayHeight);
  }

  return clampCrop(next);
}

/**
 * Pull a rectangle back to a fixed aspect, holding whichever corner is not
 * being dragged. Height follows width, except on a pure vertical drag where
 * following would ignore the gesture entirely.
 */
function enforceAspect(
  rect: CropRect,
  handle: Handle,
  aspect: number,
  displayWidth: number,
  displayHeight: number,
): CropRect {
  // The crop's aspect in normalised units is not its aspect in pixels; convert
  // through the frame's own shape.
  const normalised = aspect / (displayWidth / displayHeight);

  const vertical = handle === "n" || handle === "s";
  let { width, height } = rect;

  if (vertical) {
    width = height * normalised;
  } else {
    height = width / normalised;
  }

  // Anchor the edge that is not moving, so the crop grows away from the hand.
  const x = handle.includes("w") ? rect.x + rect.width - width : rect.x;
  const y = handle.includes("n") ? rect.y + rect.height - height : rect.y;

  return { x, y, width, height };
}
